import * as submitterService from '../../service/plagiarismReports';
import { Assessment, SectionBusinessId, Series, ReportType, EntryComponent } from '@cambridgeassessment/lib-ee-commons';
import { downloadPlagiarismReport } from '../../service/plagiarismReports/reporter';
import { NotificationActionTypes } from '../notification/types';
import { PlagiarismActionTypes } from './types';
import { displayNotification } from '../notification/actions';
import * as actions from './actions';
import {
  ProcessingError,
  ReportDetails,
  ReportDetailsQuery,
  ReportSummary,
  SearchQuery,
  SimilarityParams,
} from '../../types/Plagiarism';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '..';
import { getSeriesResponse } from '../../service/series';
import { isEmpty } from 'lodash';
import { SortingParams } from '../../pages/Content/MalpracticeReports/components/ReportTable/ReportTable';
import { buildReportName, downloadHandler } from '../../util/downloadHandler';

type ActionTypes = PlagiarismActionTypes | NotificationActionTypes;
type Dispatch = ThunkDispatch<RootState, never, ActionTypes>;
type GetState = () => RootState;

export const getSeriesData =
  (searchQuery?: SearchQuery) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    try {
      dispatch(actions.getSeries());
      const { businessUnit } = getState().userData;
      if (!businessUnit) throw new Error('BusinessId is not assigned');

      const requestData = await getSeriesResponse(businessUnit.id);

      if (searchQuery?.seriesId) {
        const seriesAssessments = await submitterService.getSeriesAssessments(businessUnit.id, searchQuery.seriesId);
        dispatch(actions.setAssessments(seriesAssessments));

        if (searchQuery.assessmentCode) {
          const assessmentComponents = await submitterService.getAssessmentComponents(
            businessUnit.id,
            searchQuery.seriesId,
            searchQuery.assessmentCode,
          );
          dispatch(actions.setAssessmentComponents(assessmentComponents));
        }
      }

      dispatch(actions.setSeries(requestData));
    } catch (err) {
      dispatch(displayNotification('Failed to get Series data'));
    }
  };

export const getAssessmentData =
  (seriesId: Series['id']) =>
  async (dispatch: Dispatch, getState: GetState): Promise<Assessment[] | undefined> => {
    try {
      const { businessUnit } = getState().userData;
      if (!businessUnit) throw new Error('BusinessId is not assigned');

      dispatch(actions.getAssessments());
      const requestData = await submitterService.getSeriesAssessments(businessUnit.id, seriesId);
      dispatch(actions.setAssessments(requestData));
      return requestData;
    } catch (err) {
      dispatch(displayNotification('Failed to get assessments data'));
    }
  };

export const getAssessmentComponentsData =
  (seriesId: Series['id'], assessmentCode: Assessment['code']) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    try {
      const { businessUnit } = getState().userData;
      if (!businessUnit) throw new Error('BusinessId is not assigned');

      dispatch(actions.getAssessmentComponents());
      const requestData = await submitterService.getAssessmentComponents(businessUnit.id, seriesId, assessmentCode);

      const uniqComponents = Object.values(
        requestData.reduce((acc: { [key: string]: EntryComponent }, val) => {
          const key = `${val.number}_${val.type}_${val.name}`;

          acc[key] = val;
          return acc;
        }, {}),
      );

      dispatch(actions.setAssessmentComponents(uniqComponents));
    } catch (err) {
      dispatch(displayNotification('Failed to get components data'));
    }
  };

export const getReportData =
  (options: SearchQuery, sortingParams?: SortingParams<ReportSummary>) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    try {
      const { summary, seriesData } = getState().malpracticeReport;

      const { businessUnit } = getState().userData;
      if (!businessUnit) throw new Error('BusinessId is not assigned');

      if (summary.reportSummary && !summary.nextPageUrl) {
        return;
      }

      if (!seriesData && options.seriesId) {
        await dispatch(getSeriesData());
        await dispatch(getAssessmentData(options.seriesId));

        if (options.assessmentCode) {
          await dispatch(getAssessmentComponentsData(options.seriesId, options.assessmentCode));
        }
      }

      const { results, nextUrl, total } = await submitterService.getReportSummaryData(
        businessUnit.id,
        options,
        summary.nextPageUrl,
        sortingParams,
      );
      dispatch(actions.setReportSummary(results, total ? +total : 0, nextUrl));
    } catch (err) {
      dispatch(displayNotification('Failed to get report data'));
    }
  };

export const getReportDetailsSection =
  (sectionBid: Omit<SectionBusinessId, 'centreId' | 'learnerNumber' | 'learnerUniqueId'>) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const results = await submitterService.getReportDetailsComponents(sectionBid);

      const filteredComponent = results.find((result) => result.number === sectionBid.componentNumber);
      const filteredSeries = filteredComponent?.series.find((series) => series.id === +sectionBid.seriesId);
      const filteredSection = filteredComponent?.sections.find((section) => section.id === +sectionBid.sectionId);

      if (filteredComponent && filteredSeries && filteredSection) {
        dispatch(
          actions.setReportDetailsParams({
            seriesName: filteredSeries.name,
            sectionName: filteredSection.name,
            assessmentCode: filteredComponent.assessment.code,
            componentName: filteredComponent.name,
            componentNumber: filteredComponent.number,
          }),
        );
      }
    } catch {
      dispatch(displayNotification('Failed to get report details params'));
    }
  };

export const getReportDetails =
  (
    reportDetailsQuery: Omit<ReportDetailsQuery, 'businessUnitId'>,
    searchQuery: SearchQuery,
    similarityParams: SimilarityParams,
    sortingParams?: SortingParams<ReportDetails>,
  ) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { businessUnit } = getState().userData;
      const { details } = getState().malpracticeReport;

      if (!businessUnit) throw new Error('BusinessId is not assigned');

      if (details.similarityRanges && !details.nextPageUrl) {
        return;
      }

      const { results, nextUrl, total } = await submitterService.getReportDetailsData(
        { ...reportDetailsQuery, businessUnitId: businessUnit.id },
        { ...searchQuery, ...similarityParams },
        sortingParams,
        details.nextPageUrl,
      );

      dispatch(actions.setReportDetails(results, total ? +total : 0, nextUrl));
    } catch {
      dispatch(displayNotification('Failed to get report details data'));
    }
  };

interface Params {
  seriesId: SectionBusinessId['seriesId'];
  assessmentCode: SectionBusinessId['assessmentCode'];
  componentNumber: SectionBusinessId['componentNumber'];
  sectionId: SectionBusinessId['sectionId'];
}

interface Options {
  centreId?: SectionBusinessId['centreId'];
  learnerNumber?: SectionBusinessId['learnerNumber'];
}

export const getErrorReports =
  (params: Params, options: Options, sortingParams?: SortingParams<ProcessingError>) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { businessUnit } = getState().userData;
      if (!businessUnit) throw new Error('BusinessId is not assigned');

      const { details } = getState().malpracticeReport;
      if (!isEmpty(details.processingErrors) && !details.nextPageUrl) {
        return;
      }

      const { results, nextUrl } = await submitterService.getFailedReportDetails(
        { businessUnitId: businessUnit.id, ...params },
        options,
        sortingParams,
        details.nextPageUrl,
      );

      dispatch(actions.setProcessingErrorsList(results, nextUrl));
    } catch {
      dispatch(displayNotification('Failed to get report details data'));
    }
  };

export const getTurnitinUrl =
  (submissionId: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const { viewerUrl } = await submitterService.viewTurnitinReport(submissionId);
      window.open(viewerUrl);
    } catch {
      dispatch(displayNotification('Failed to get Turnitin url'));
    }
  };

export const getReport =
  (seriesId: Series['id'], seriesName: Series['name'], reportType: ReportType, query?: SearchQuery) =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    try {
      const { businessUnit } = getState().userData;
      if (!businessUnit) throw new Error('BusinessId is not assigned');

      const data = await downloadPlagiarismReport(businessUnit.id, seriesId, reportType, query);
      const reportName = buildReportName(reportType, seriesName, query);
      downloadHandler(data, reportName);
    } catch (error) {
      dispatch(displayNotification('Failed to download report'));
    }
  };

export default {
  getSeriesData,
  getAssessmentData,
  getAssessmentComponentsData,
  getReportData,
  getReportDetails,
  getReportDetailsSection,
  getTurnitinUrl,
  getReport,
};
